import React from 'react';
import { CloudIcon, HeartIcon, SparklesIcon, SunIcon, StarIcon, ShieldCheckIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { FONTS, HeroCarousel, BenefitsCTA, ActivitiesCTA, PhotoGallery, ReadyToStart } from './ServicePageTemplate';
import { Link } from 'react-router-dom';

// ─── IMPORT STEAM PHOTOS (using your steam room.png) ─────────────────────────
import steamRoom from '../../assets/images/steam room.png';

// ─── PHOTOS ARRAY FOR HERO SLIDER (using steam room image repeated) ──────────
const HERO_PHOTOS = [steamRoom, steamRoom, steamRoom, steamRoom];

// ─── PHOTOS FOR GALLERY - Using only steam pics ──────────────────────────────
const GALLERY_PHOTOS = [steamRoom, steamRoom, steamRoom, steamRoom, steamRoom, steamRoom];

const DATA = {
  tagline:   'BREATHE DEEP. FEEL ALIVE.',
  heroText: 'Warm, eucalyptus-infused steam that opens airways, hydrates skin and melts tension from within.',
  waMessage: encodeURIComponent("Hi PowerCore! 💨 I'm interested in the Steam Room. Could you tell me more about access, pricing and how it works?"),

  features: [
    { icon: <CloudIcon className="h-6 w-6" />,       title: 'Eucalyptus Steam',      desc: 'Pure eucalyptus oil infusions transform every breath into a therapeutic, aromatic experience. The natural compounds in eucalyptus actively support respiratory health and deep relaxation.' },
    { icon: <HeartIcon className="h-6 w-6" />,        title: 'Respiratory Relief',    desc: 'Moist steam opens bronchial passages, relieves sinus congestion and eases breathing. Perfect for those prone to allergies, asthma, seasonal illness or post-exercise breathlessness.' },
    { icon: <SparklesIcon className="h-6 w-6" />,     title: 'Deep Skin Hydration',   desc: 'Unlike dry heat, steam floods skin cells with moisture — plumping the dermis, improving elasticity and leaving skin deeply hydrated, visibly soft and glowing after every session.' },
    { icon: <SunIcon className="h-6 w-6" />,          title: 'Joint & Muscle Relief', desc: 'Warm, humid air relaxes connective tissue and reduces joint stiffness from arthritis, intense training or long periods of sitting. Your joints and muscles will feel years younger.' },
    { icon: <ShieldCheckIcon className="h-6 w-6" />,  title: 'Circulatory Boost',     desc: 'Steam heat dilates blood vessels and improves peripheral circulation — delivering oxygen-rich blood to muscles and organs more efficiently, boosting energy and accelerating recovery.' },
    { icon: <StarIcon className="h-6 w-6" />,         title: 'Stress Dissolution',    desc: 'The warm cocoon triggers a deep parasympathetic response — heart rate slows, muscles release and mental chatter quiets completely. Leave each session feeling totally reset.' },
  ],

  benefits: [
    'Opens nasal passages and improves breathing clarity',
    'Deeply hydrates and plumps skin naturally',
    'Relieves joint stiffness and chronic muscle tension',
    'Accelerates metabolic rate and passive calorie burn',
    'Boosts circulation and oxygen delivery to muscles',
    'Reduces cortisol and stress hormones significantly',
    'Complements gym training with powerful active recovery',
    'Aromatherapy benefits from natural eucalyptus infusion',
  ],
};

// Custom Features Grid with Steam Room Background
const FeaturesGridWithBackground = ({ features, sectionTitle, sectionLabel }) => (
  <section className="py-16 relative overflow-hidden">
    {/* Background Image with steamRoom */}
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{ 
        backgroundImage: `url(${steamRoom})`,
      }}
    />
    
    <div className="container-custom relative z-10">
      <div className="text-center mb-12">
        <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block text-teal-300">
          {sectionLabel}
        </span>
        <h2 className="font-black text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
          {sectionTitle}
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((f, i) => (
          <div key={i}
            className="rounded-3xl p-7 transition-all duration-300 group"
            style={{ 
              background: 'rgba(0,0,0,0.75)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110"
              style={{ background: 'rgba(20,184,166,0.2)', color: '#5eead4' }}>
              {f.icon}
            </div>
            <h3 className="font-black text-white mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem' }}>
              {f.title.toUpperCase()}
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SteamService = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
    <style>{FONTS}</style>
    
    {/* 1. Hero Slider - Using steam room image */}
    <HeroCarousel photos={HERO_PHOTOS} {...DATA} />
    
    {/* 2. Photo Gallery - EXPLORE STEAM (second section after hero) */}
    <PhotoGallery photos={GALLERY_PHOTOS} title="EXPLORE STEAM" />
    
    {/* 3. Features Grid with Steam Room Background */}
    <FeaturesGridWithBackground 
      features={DATA.features} 
      sectionLabel="What We Offer" 
      sectionTitle="THE POWER OF THERAPEUTIC STEAM" 
    />
    
    {/* 4. Benefits CTA */}
    <BenefitsCTA benefits={DATA.benefits} benefitsTitle="BREATHE BETTER. FEEL BETTER."
      ctaTitle="BOOK YOUR SESSION" ctaText="Message us on WhatsApp to book your steam session or ask about combined gym and steam packages."
      waMessage={DATA.waMessage} />
    
    {/* 5. Activities CTA */}
    <ActivitiesCTA />
    
    {/* 6. Steam Room Benefits, Hours & Pricing Section */}
    <div className="py-16 bg-gradient-to-br from-teal-900 to-cyan-800">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-teal-300 font-semibold tracking-wider uppercase text-sm">Wellness Investment</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 mt-2">
              EXPERIENCE THE <span className="text-teal-300">HEALING STEAM</span>
            </h2>
            <p className="text-teal-100 mb-6 leading-relaxed">
              Whether you're looking to clear your sinuses, hydrate your skin, 
              or simply melt away stress, our eucalyptus-infused steam room offers 
              the ultimate therapeutic escape.
            </p>
            
            {/* Steam Room Hours */}
            <div className="mb-6 p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="h-5 w-5 text-teal-300" />
                <h3 className="text-white font-bold text-lg">Steam Room Operating Hours</h3>
              </div>
              <div className="space-y-2 text-teal-100">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Monday - Saturday:</span>
                  <span>5:00 AM - 8:00 AM &amp; 5:00 PM - 8:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Sunday:</span>
                  <span>8:00 AM - 8:00 PM</span>
                </div>
              </div>
              <p className="text-teal-300/70 text-xs mt-3 italic">*Last session 30 minutes before closing</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white">
                <span className="text-teal-300 text-xl">✓</span>
                <span>Included with all premium memberships</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <span className="text-teal-300 text-xl">✓</span>
                <span>Day passes available for non-members</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <span className="text-teal-300 text-xl">✓</span>
                <span>Eucalyptus oil infusion for aromatherapy</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <span className="text-teal-300 text-xl">✓</span>
                <span>Towels and refreshments available</span>
              </li>
            </ul>
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 hover:scale-105"
            >
              View Pricing <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img 
              src={steamRoom} 
              alt="Steam room interior at PowerCore Fitness" 
              className="rounded-2xl shadow-xl h-48 w-full object-cover"
            />
            <img 
              src={steamRoom} 
              alt="Steam room relaxation area" 
              className="rounded-2xl shadow-xl h-48 w-full object-cover mt-8"
            />
          </div>
        </div>
      </div>
    </div>
    
    {/* 7. Ready to Start CTA */}
    <ReadyToStart />
  </div>
);

export default SteamService;